import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material'
import { ThemeName, VeilStreamThemeContextValue } from './types'
import { themes, themeNames, DEFAULT_THEME_NAME, DEFAULT_THEME_STORAGE_KEY } from './themes'
import { StorageAdapter, noOpStorageAdapter } from '../../utils/storage'

/**
 * VeilStream theme context
 */
const VeilStreamThemeContext = createContext<VeilStreamThemeContextValue | undefined>(undefined)

/**
 * Props for VeilStreamThemeProvider
 */
export interface VeilStreamThemeProviderProps {
  /** Child components */
  children: ReactNode

  /**
   * Storage adapter for theme persistence.
   * If not provided, theme changes will not be persisted.
   * Pass `localStorageAdapter` from '@veilstream/ui' for browser localStorage persistence.
   */
  storage?: StorageAdapter

  /**
   * Storage key for theme persistence.
   * @default 'veilstream-theme'
   */
  storageKey?: string

  /**
   * Default theme to use if no stored theme is found.
   * @default 'Evergreen Dark'
   */
  defaultTheme?: ThemeName

  /**
   * Include MUI CssBaseline component for CSS reset.
   * @default true
   */
  includeCssBaseline?: boolean
}

/**
 * VeilStream Theme Provider
 *
 * Wraps MUI ThemeProvider and provides VeilStream theme management with optional persistence.
 *
 * @example
 * // With localStorage persistence
 * import { VeilStreamThemeProvider, localStorageAdapter } from '@veilstream/ui'
 *
 * function App() {
 *   return (
 *     <VeilStreamThemeProvider storage={localStorageAdapter}>
 *       <YourApp />
 *     </VeilStreamThemeProvider>
 *   )
 * }
 *
 * @example
 * // Without persistence (theme resets on page reload)
 * import { VeilStreamThemeProvider } from '@veilstream/ui'
 *
 * function App() {
 *   return (
 *     <VeilStreamThemeProvider>
 *       <YourApp />
 *     </VeilStreamThemeProvider>
 *   )
 * }
 */
export function VeilStreamThemeProvider({
  children,
  storage = noOpStorageAdapter,
  storageKey = DEFAULT_THEME_STORAGE_KEY,
  defaultTheme = DEFAULT_THEME_NAME,
  includeCssBaseline = true,
}: VeilStreamThemeProviderProps) {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>(defaultTheme)

  // Load theme from storage on mount
  useEffect(() => {
    const savedTheme = storage.getItem(storageKey)
    if (savedTheme && themes[savedTheme as ThemeName]) {
      setCurrentTheme(savedTheme as ThemeName)
    }
  }, [storage, storageKey])

  // Function to change theme
  const setTheme = (themeName: ThemeName) => {
    setCurrentTheme(themeName)
    storage.setItem(storageKey, themeName)
  }

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue: VeilStreamThemeContextValue = useMemo(
    () => ({
      currentTheme,
      setTheme,
      availableThemes: themeNames,
      theme: themes[currentTheme],
    }),
    [currentTheme]
  )

  return (
    <VeilStreamThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={themes[currentTheme]}>
        {includeCssBaseline && <CssBaseline />}
        {children}
      </MuiThemeProvider>
    </VeilStreamThemeContext.Provider>
  )
}

/**
 * Hook to access VeilStream theme context
 *
 * @returns Theme context value with current theme and setter
 * @throws Error if used outside VeilStreamThemeProvider
 *
 * @example
 * function ThemeSwitcher() {
 *   const { currentTheme, setTheme, availableThemes } = useVeilStreamTheme()
 *
 *   return (
 *     <select value={currentTheme} onChange={(e) => setTheme(e.target.value as ThemeName)}>
 *       {availableThemes.map((name) => (
 *         <option key={name} value={name}>
 *           {name}
 *         </option>
 *       ))}
 *     </select>
 *   )
 * }
 */
export function useVeilStreamTheme(): VeilStreamThemeContextValue {
  const context = useContext(VeilStreamThemeContext)
  if (context === undefined) {
    throw new Error('useVeilStreamTheme must be used within a VeilStreamThemeProvider')
  }
  return context
}
